import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import QueryString from 'qs';

import { getPaymentHistory } from '../../../api/user';
import CommonLoading from '../../../form-components/CommonLoading';
import CommonPagination from '../../../form-components/CommonPagination';
import ManualRefundModalV2 from '../../manualSales/manualModal/manualRefundModalV2';
import ManualDetailModalV2 from '../../manualSales/manualModal/manualDetailModalV2';

import { PageWrapper, TotalCount } from './UserPaymentHistoryStyled';
import UserPaymentHistorySummaryTable from './UserPaymentHistorySummaryTable';
import UserPaymentHistoryTable from './UserPaymentHistoryTable';

const PAGE_SIZE = 20;

// 결제 내역 행을 모달이 요구하는 형태로 변환.
// memberOrderId !== lastPaidMemberOrderId 이거나 PRODUCT 결제는 변경 비활성화.
const buildTicketData = (row, extra = {}) => {
  const memberOrderIdStr = String(row.memberOrderId);
  const lastPaidMemberOrderIdStr = String(row.lastPaidMemberOrderId);

  return {
    memberOrderId: row.memberOrderId,
    courseTicketName: row.courseTicketName,
    payType: row.payType,
    paidDate: row.paidDate,
    finalPrice: row.finalPrice,
    ticketId: row.ticketId || row.courseTicketId,
    usedCount: row.usedCount || 0,
    outstandingAmount: row.outstandingAmount,
    lastPaidMemberOrderId: row.lastPaidMemberOrderId,
    totalOrderPaymentAmount: row.totalOrderPaymentAmount,
    isChangeDisabled: memberOrderIdStr !== lastPaidMemberOrderIdStr || row.payType === 'PRODUCT',
    ...extra,
  };
};

const UserPaymentHistory = forwardRef(({ id }, ref) => {
  const toast = useToast();
  const user = useSelector(state => state.user.user);
  const queryClient = useQueryClient();

  const headBranchId = user.headBranch?.headBranchId;
  const branchId = user.branch?.branchId;

  const [query, setQuery] = useState({
    page: 1,
    limit: PAGE_SIZE,
    headBranchId,
    branchId,
  });

  // 지점 스코프는 마운트 이후에 정해질 수 있다(지점 미선택 상태로 회원 상세 URL 직접 진입 →
  // 그 뒤 상단에서 센터/지점 선택). useState 초기값은 한 번만 평가되고 queryKey 에 query 가
  // 물려 있어, 동기화하지 않으면 branchId=undefined 로 고정돼 목록이 영구히 비어 보인다.
  useEffect(() => {
    setQuery(prev =>
      prev.headBranchId === headBranchId && prev.branchId === branchId
        ? prev
        : { ...prev, page: 1, headBranchId, branchId },
    );
  }, [headBranchId, branchId]);

  const [selectedTicketData, setSelectedTicketData] = useState(null);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const queryKey = ['userPaymentHistory', id, query];

  const { data: paymentResponse, isLoading } = useQuery({
    queryKey,
    queryFn: () => getPaymentHistory(id, QueryString.stringify(query), user.token),
    enabled: !!id && !!user.token,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['userPaymentHistory', id] });

  // UserDetail에서 ref로 호출 — 결제 모달 닫힌 뒤 외부에서 새로고침할 때 사용.
  useImperativeHandle(ref, () => ({
    refreshData: async () => {
      try {
        await refresh();
      } catch (error) {
        console.error('Failed to refresh payment history:', error);
      }
    },
  }));

  const handleClickEdit = row => {
    if (!user.branch?.branchId) {
      toast.error('지점 정보가 없습니다');
      return;
    }
    setSelectedTicketData(buildTicketData(row, { memberId: id }));
    setIsRefundModalOpen(true);
  };

  const handleClickRefundDetail = row => {
    setSelectedTicketData(buildTicketData(row, { updateType: 'refund', memberId: id }));
    setIsDetailModalOpen(true);
  };

  const handleClickPayDetail = row => {
    setSelectedTicketData(buildTicketData(row, { updateType: 'pay', memberId: id }));
    setIsDetailModalOpen(true);
  };

  const handleRefundModalClose = () => {
    setIsRefundModalOpen(false);
    setSelectedTicketData(null);
  };

  const handleDetailModalClose = () => {
    setIsDetailModalOpen(false);
    setSelectedTicketData(null);
  };

  const handleChangePage = (_, value) => setQuery(prev => ({ ...prev, page: value }));

  // 첫 진입 시 결제 합계/내역이 빈 상태로 잠깐 보이는 것을 막는다.
  // 페이지네이션 등 추가 로딩에는 적용하지 않음 — 기존 데이터 유지가 더 자연스럽기 때문.
  if (isLoading && !paymentResponse) {
    return (
      <PageWrapper>
        <CommonLoading minHeight={480} />
      </PageWrapper>
    );
  }

  return (
    <>
      <PageWrapper>
        <UserPaymentHistorySummaryTable totalPayment={paymentResponse?.totalPayMap} />

        <TotalCount>총 {(paymentResponse?.totalElements ?? 0).toLocaleString()}건</TotalCount>

        <UserPaymentHistoryTable
          rows={paymentResponse?.content}
          onClickEdit={handleClickEdit}
          onClickRefundDetail={handleClickRefundDetail}
          onClickPayDetail={handleClickPayDetail}
        />

        <CommonPagination
          totalElements={paymentResponse?.totalElements}
          limit={query.limit}
          page={query.page}
          onChange={handleChangePage}
        />
      </PageWrapper>

      <ManualRefundModalV2
        ticketData={selectedTicketData}
        open={isRefundModalOpen}
        onClose={handleRefundModalClose}
        fetchData={refresh}
      />
      <ManualDetailModalV2
        ticketData={selectedTicketData}
        open={isDetailModalOpen}
        onClose={handleDetailModalClose}
        fetchData={refresh}
      />
    </>
  );
});

UserPaymentHistory.displayName = 'UserPaymentHistory';

export default UserPaymentHistory;
